import { useEffect, useState } from 'react'
import { Mail } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { requestEmailChange, verifyEmailChange } from '@/lib/members'
import { getApiErrorMessage } from '@/lib/api'

type EmailDelivery = 'sent' | 'failed' | 'logged_only'
type Step = 'idle' | 'sent'

interface EmailAcknowledge {
  emailDelivery?: EmailDelivery
  emailError?: string | null
  devOtp?: string | null
}

interface ProfileEmailVerifyProps {
  email: string
  savedEmail: string
  onVerifiedChange: (verified: boolean) => void
}

function normalizeEmail(raw: string): string {
  return raw.trim().toLowerCase()
}

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
}

function maskEmail(email: string): string {
  const [local, domain] = email.split('@')
  if (!local || !domain) return email
  if (local.length <= 2) return `${local[0] ?? ''}···@${domain}`
  return `${local.slice(0, 2)}···@${domain}`
}

function EmailStatusCard({ email, ack }: { email: string; ack: EmailAcknowledge }) {
  const { emailDelivery, emailError, devOtp } = ack

  if (emailDelivery === 'sent') {
    return (
      <div
        className="rounded border border-status-paid-bg bg-status-paid-bg/40 px-4 py-3 font-sans text-sm text-status-paid-text"
        role="status"
      >
        <p className="font-medium">Email sent</p>
        <p className="mt-1 text-xs leading-relaxed">
          A 6-digit code was sent to <strong>{maskEmail(email)}</strong>. It expires in 10 minutes.
          Check your inbox and spam folder.
        </p>
      </div>
    )
  }

  if (emailDelivery === 'failed') {
    return (
      <div className="space-y-3">
        <div
          className="rounded border border-destructive/30 bg-destructive/10 px-4 py-3 font-sans text-sm text-destructive"
          role="alert"
        >
          <p className="font-medium">Email could not be delivered</p>
          {emailError && <p className="mt-1 text-xs leading-relaxed">{emailError}</p>}
        </div>
        {devOtp && (
          <p className="font-sans text-xs text-status-pending-text">
            Dev code: <strong className="text-sm">{devOtp}</strong>
          </p>
        )}
      </div>
    )
  }

  if (emailDelivery === 'logged_only') {
    return (
      <div className="space-y-3">
        <div
          className="rounded border border-status-pending-bg bg-status-pending-bg/40 px-4 py-3 font-sans text-sm text-status-pending-text"
          role="status"
        >
          <p className="font-medium">Email not configured (local dev)</p>
          <p className="mt-1 text-xs leading-relaxed">
            No message was sent to {maskEmail(email)}. Check the API terminal for the code.
          </p>
        </div>
        {devOtp && (
          <p className="font-sans text-xs text-status-pending-text">
            Dev code: <strong className="text-sm">{devOtp}</strong>
          </p>
        )}
      </div>
    )
  }

  return null
}

export function ProfileEmailVerify({
  email,
  savedEmail,
  onVerifiedChange,
}: ProfileEmailVerifyProps) {
  const normalizedEmail = normalizeEmail(email)
  const normalizedSaved = normalizeEmail(savedEmail)
  const emailChanged = normalizedEmail.length > 0 && normalizedEmail !== normalizedSaved

  const [step, setStep] = useState<Step>('idle')
  const [verifiedForEmail, setVerifiedForEmail] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const [ack, setAck] = useState<EmailAcknowledge | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [requestPending, setRequestPending] = useState(false)
  const [verifyPending, setVerifyPending] = useState(false)

  const isVerified = emailChanged && verifiedForEmail === normalizedEmail

  useEffect(() => {
    onVerifiedChange(emailChanged ? isVerified : true)
  }, [emailChanged, isVerified, onVerifiedChange])

  useEffect(() => {
    if (!emailChanged) {
      setStep('idle')
      setVerifiedForEmail(null)
      setCode('')
      setAck(null)
      setError(null)
    }
  }, [emailChanged])

  if (!emailChanged) return null

  const canRequest = isValidEmail(normalizedEmail)

  async function handleRequest() {
    setError(null)
    setRequestPending(true)
    setVerifiedForEmail(null)
    try {
      const result = await requestEmailChange(email.trim())
      setAck({
        emailDelivery: result.emailDelivery,
        emailError: result.emailError,
        devOtp: result.devOtp,
      })
      setStep('sent')
      setCode('')
    } catch (err) {
      setError(getApiErrorMessage(err))
    } finally {
      setRequestPending(false)
    }
  }

  async function handleVerify() {
    if (code.length !== 6) return
    setError(null)
    setVerifyPending(true)
    try {
      await verifyEmailChange(email.trim(), code.trim())
      setVerifiedForEmail(normalizedEmail)
      setStep('idle')
    } catch (err) {
      setError(getApiErrorMessage(err))
    } finally {
      setVerifyPending(false)
    }
  }

  if (isVerified) {
    return (
      <div
        className="rounded border border-status-paid-bg bg-status-paid-bg/40 px-4 py-3 font-sans text-sm text-status-paid-text"
        role="status"
      >
        <p className="font-medium">Email address verified</p>
        <p className="mt-1 text-xs leading-relaxed">
          You can save your profile to update your email to {maskEmail(normalizedEmail)}.
        </p>
      </div>
    )
  }

  return (
    <div
      className="space-y-4 rounded border-2 border-primary/25 bg-green-pale/50 px-5 py-5"
      role="region"
      aria-label="Email verification required"
    >
      <div className="flex gap-3">
        <div
          className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary"
          aria-hidden
        >
          <Mail size={20} strokeWidth={1.75} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-sans text-sm font-semibold text-ink">Verify new email address</p>
          <p className="mt-1 font-sans text-sm leading-relaxed text-muted-foreground">
            Save is blocked until you confirm{' '}
            <strong className="font-medium text-ink">{maskEmail(normalizedEmail)}</strong>. We will
            email a 6-digit code to that address.
          </p>
        </div>
      </div>

      {step === 'idle' && (
        <Button
          type="button"
          className="w-full sm:w-auto"
          loading={requestPending}
          disabled={!canRequest}
          onClick={() => void handleRequest()}
        >
          Send verification code
        </Button>
      )}

      {!canRequest && step === 'idle' && (
        <p className="font-sans text-sm text-muted-foreground">
          Enter a valid email address to send a code.
        </p>
      )}

      {step === 'sent' && ack && <EmailStatusCard email={normalizedEmail} ack={ack} />}

      {step === 'sent' && (
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[140px] flex-1">
            <Input
              label="Verification code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  void handleVerify()
                }
              }}
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="000000"
            />
          </div>
          <Button
            type="button"
            loading={verifyPending}
            disabled={code.length !== 6}
            className="min-w-[7rem]"
            onClick={() => void handleVerify()}
          >
            Verify code
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={requestPending}
            onClick={() => void handleRequest()}
          >
            Resend code
          </Button>
        </div>
      )}

      {error && (
        <p className="font-sans text-sm text-destructive" role="alert">
          {error}
        </p>
      )}
    </div>
  )
}
